import React, { useState } from 'react';
import { type FullName } from 'src/types/users';
import { formatFullName } from 'src/utils/formatting/name';
import { PiCheckLight, PiXLight } from 'react-icons/pi';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Loading from 'src/modules/shared/components/atoms/loading';

interface InlineFullNameInputProps extends FullName {
  onSave: (newValues: FullName) => Promise<boolean>;
  loading?: boolean;
}

/**
 * An inline-editable full name input component that allows editing both first and last names.
 * Provides edit/save/cancel functionality with loading state.
 *
 * @param firstName - The first name value
 * @param lastName - The last name value
 * @param onSave - Callback function when saving new values
 * @param loading - Whether the component is in a loading state
 */
export const InlineFullNameInput: React.FC<InlineFullNameInputProps> = ({
  firstName,
  lastName,
  onSave,
  loading = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValues, setInputValues] = useState<FullName>({
    firstName,
    lastName,
  });
  const [originalValues] = useState<FullName>({ firstName, lastName });

  const handleSave = async () => {
    const success = await onSave(inputValues);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setInputValues(originalValues);
    setIsEditing(false);
  };

  const handleChange = (field: keyof FullName, newValue: string) => {
    setInputValues((prevValues) => ({
      ...prevValues,
      [field]: newValue,
    }));
  };

  const displayValue = formatFullName(firstName, lastName);

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 max-w-fit">
        <TextInput
          value={inputValues.firstName ?? ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange('firstName', e.target.value)
          }
          // TODO: check when implementing i18n
          placeholder="Nombre"
          size="sm"
          disabled={loading}
          className="w-32"
        />
        <TextInput
          value={inputValues.lastName ?? ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange('lastName', e.target.value)
          }
          // TODO: check when implementing i18n
          placeholder="Apellido"
          size="sm"
          disabled={loading}
          className="w-32"
        />
        {loading ? (
          <Loading size="sm" variant="primary" inline />
        ) : (
          <div className="flex items-center gap-1">
            <Button
              variant="success"
              appearance="soft"
              size="sm"
              onClick={handleSave}
              disabled={loading}
              isIconOnly
            >
              <PiCheckLight className="h-5 w-5" />
            </Button>
            <Button
              variant="danger"
              appearance="soft"
              size="sm"
              onClick={handleCancel}
              disabled={loading}
              isIconOnly
            >
              <PiXLight className="h-5 w-5" />
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsEditing(true)}
      className="text-sm hover:bg-gray-100 px-2 py-1 rounded transition-all text-left"
    >
      {displayValue}
    </button>
  );
};

export default InlineFullNameInput;
